import { cn } from '../../lib/cn';

export interface TabItem<TValue extends string> {
  value: TValue;
  label: string;
  /** Shown as a quiet count next to the label. */
  badge?: number;
}

export interface TabsProps<TValue extends string> {
  items: readonly TabItem<TValue>[];
  value: TValue;
  onChange: (value: TValue) => void;
  className?: string;
}

/**
 * The underlined tab bar used on detail pages and the Conditions module. Borders use logical
 * properties so the active indicator stays under the tab in both reading directions.
 */
export const Tabs = <TValue extends string>({ items, value, onChange, className }: TabsProps<TValue>) => (
  <div
    role="tablist"
    className={cn('flex gap-1 border-b border-border', className)}
  >
    {items.map((item) => {
      const isActive = item.value === value;

      return (
        <button
          key={item.value}
          type="button"
          role="tab"
          aria-selected={isActive}
          onClick={() => onChange(item.value)}
          className={cn(
            '-mb-px inline-flex items-center gap-2 border-b-2 px-3 py-2 text-sm font-medium transition-colors',
            isActive ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:border-border hover:text-foreground',
          )}
        >
          {item.label}
          {item.badge === undefined ? null : <span className="text-xs font-normal text-muted-foreground">{item.badge}</span>}
        </button>
      );
    })}
  </div>
);
